import { useCapTableTransactions, useStockClasses } from '@commenda/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Icons,
} from '@commenda/kakashi';
import { TEquityInfoKS } from '@commenda/types/src/incorporation/types';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { IncorporationFormHeading, SummaryFieldKS } from '../../common';
import CorporateShareholdersSummary from '../../common/CorporateShareholdersSummary';
import {
  calculateStakeholdersEquityMap,
  extractCorporateShareholders,
} from '../../common/corporateStakeholder/utils';
import CapTable from '../../EquityInfo/CapTable';
import { TShareholderVsSharePercentageMap } from '../../EquityInfo/types';
import { generateOwnershipSummaryTableRowsForUAE } from '../Equity/utils';
import { SummaryBusinessDetails } from '../Summary/BusinessDetails';
import { SummaryEntityNames } from '../Summary/EntityNames';
import { UAEDirectorField } from '../Summary/UAEDirectorField';
import { TUAEIncorporationRequestData } from '../types';

type TSummaryProps = {
  workRequest: WorkRequestResponseType & {
    requestData: TUAEIncorporationRequestData;
  };
};

export const AEAdminSummarySection = ({ workRequest }: TSummaryProps) => {
  const router = useRouter();

  const { data: capTableTransactions } = useCapTableTransactions(
    workRequest.corporationId
  );
  const { data: stockClasses } = useStockClasses(workRequest.corporationId);

  const [equityInfo, setEquityInfo] = useState<TEquityInfoKS>({
    shareCount: null,
    price: null,
    shareRetained: null,
  });

  const [ownershipSummaryTableData, setOwnershipSummaryTableData] = useState<
    {
      id: string;
      name: string;
      ownership: string;
      actualOwnership: string;
      esopContribution: string;
      shares: string;
    }[]
  >([]);

  const [selectedStakeholders, setSelectedStakeholders] =
    useState<TShareholderVsSharePercentageMap>();

  // we can pick the first stock class here on the corporation level, as when the incorporation process is ongoing
  // we will only have one stock class
  const stockClass = stockClasses?.[0];

  const getOwnershipSummaryTable = () => {
    const ownershipSummaryTableRows = generateOwnershipSummaryTableRowsForUAE({
      equityInfo,
      selectedStakeholders,
      corporationType: workRequest?.requestData?.corporationType,
    });

    setOwnershipSummaryTableData(ownershipSummaryTableRows);
  };

  useEffect(() => {
    getOwnershipSummaryTable();
  }, [equityInfo, selectedStakeholders, capTableTransactions]);

  useEffect(() => {
    if (!stockClass) return;
    if (!capTableTransactions) return;

    const { shareRetained, stakeholdersEquityMap, authorizedShares } =
      calculateStakeholdersEquityMap({
        capTableTransactions,
        stockClass,
      });

    setEquityInfo({
      shareCount: authorizedShares || 10_000_000,
      price:
        stockClass?.additionalInformation?.pricePerShare?.amount || 0.00001,
      shareRetained,
    });

    if (capTableTransactions?.length > 0)
      setSelectedStakeholders(stakeholdersEquityMap);
  }, [workRequest, capTableTransactions, stockClass]);

  return (
    <div className="flex flex-col gap-10">
      <IncorporationFormHeading
        text="Summary"
        additionalText="Please verify the following information before submitting. Once submitted, you may not be able to make changes."
      />

      {/* Business Description */}
      <SummaryBusinessDetails
        requestData={workRequest?.requestData}
        hideActionButtons
        setActiveStep={() => {}}
      />

      {/* Entity names */}
      <SummaryEntityNames
        requestData={workRequest?.requestData}
        hideActionButtons
        setActiveStep={() => {}}
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4 className="body-16-bold text-text-primary">
            Shareholders and Key Position Holders
          </h4>
        </div>
        <div className="flex flex-col gap-3 rounded-sm">
          <Accordion
            className="AccordionRoot flex flex-col gap-3"
            type="single"
            collapsible
          >
            {workRequest?.directors
              ?.filter((eachDirector) =>
                eachDirector.corporationHasDirector.find(
                  (chd) =>
                    chd.corporationId === workRequest.corporationId &&
                    chd.directorId === eachDirector.id &&
                    (chd.additionalDirectorInformation?.isDirector ||
                      capTableTransactions?.some(
                        (tx) =>
                          tx.stakeholding?.stakeholder?.director?.id ===
                          eachDirector.id
                      ) ||
                      chd.additionalDirectorInformation?.boardPosition?.length)
                )
              )
              .map((dir) => (
                <AccordionItem
                  className="AccordionItem border border-border-primary rounded-md"
                  value={dir.id?.toString()}
                >
                  <AccordionTrigger className="w-full ">
                    <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                      <span className="text-base ">
                        {dir?.directorFirstName} {dir?.directorLastName}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="p-4 pt-0">
                    <UAEDirectorField
                      key={dir.id}
                      director={dir}
                      data={workRequest}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h4 className="txt-medium-bold">Company Ownership</h4>
        {ownershipSummaryTableData && ownershipSummaryTableData.length > 0 ? (
          <>
            <div className="flex flex-col gap-3 bg-bg-disabled-subtle rounded-sm p-6">
              <SummaryFieldKS
                heading="Paid Up Capital"
                value={`AED ${workRequest?.requestData?.paidUpCapital}`}
              />
            </div>
            <div className="flex flex-col pt-2 gap-4 mt-4">
              <CapTable
                data={ownershipSummaryTableData}
                showEsopPool={false}
                isNumberOfSharesApplicable
              />
            </div>
          </>
        ) : (
          <p className="text-text-secondary mt-4">No ownership data available.</p>
        )}
      </div>

      {extractCorporateShareholders(capTableTransactions).length > 0 ? (
        <div className="flex flex-col gap-3">
          <h4 className="body-16-bold text-text-primary">
            Corporate Shareholders
          </h4>
        <CorporateShareholdersSummary
          corporateShareholders={extractCorporateShareholders(
            capTableTransactions
          )}
          />
        </div>
      ) : null}

      <Button className="w-fit" onClick={router.back}>
        <Icons.ArrowRight className="rotate-180 mr-2 h-4 w-4" />
        <span>Back to Home</span>
      </Button>
    </div>
  );
};
